import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_close_default } from '@repo/icons/index'
import { addDays, addMonths } from 'date-fns'
import { debounce } from 'lodash'
import Cookies from 'universal-cookie'
import { GTM_EVENT, sendGTMEvent } from '../../helpers/gtmHelper'
import { APP_SCREEN_NAME } from '../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../modules/hooks/common/useIsMobile'
import { PROMO_POPUP_HIDE_DURATION } from '../../modules/hooks/common/useRemoteConfigPopup'
import useReturnFullUrl from '../../modules/hooks/webview/common/useReturnFullUrl'
import { s_upcoming_lessons } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import { getDday } from '../../modules/utils'
import { COOKIE_OPT } from '../../modules/vars'
import usePopupStore from '../../store/usePopupStore'
import { D_DAY_TYPE, DdayCounter } from './DdayCounter'

export interface IPopupData {
  type: string
  id: string
  enabled: boolean
  enabled_url_list: string[]
  display_period: DisplayPeriod
  button: Button
  popup_background_image: PopupBackgroundImage
  d_day_counter: DDayCounter
}

export interface Button {
  url: string
  app_url: string
  text: Text
  text_color: string
  text_bg_color: string
  do_not_see_button_color: string
  close_modal_x_button_color: string
}

export interface Text {
  ko: string
  en: string
}

export interface DDayCounter {
  enabled: boolean
  gap_from_top_pixel_desktop: number
  gap_from_top_pixel_mobile: number
  year: number
  month: number
  day: number
  hour: number
  text_color: string
}

export interface DisplayPeriod {
  start_date: Date
  end_date: Date
}

export interface PopupBackgroundImage {
  desktop: Text
  mobile: Text
}

interface IRemoteConfigPopupBody {
  popupData: IPopupData
}

const RemoteConfigPopupBody = ({ popupData }: IRemoteConfigPopupBody) => {
  const cookies = new Cookies()
  const { t, isLocaleKO } = useTranslation()
  const router = useAppRouter()
  const isMobile = useIsMobile()

  const getFullUrl = useReturnFullUrl()
  const popupClose = usePopupStore((state) => state.close)

  const dueDate = Date.UTC(
    popupData?.d_day_counter?.year,
    popupData?.d_day_counter?.month - 1,
    popupData?.d_day_counter?.day,
    popupData?.d_day_counter?.hour
  )
  const dday = getDday(dueDate)
  const date = new Date(dueDate)

  const digitClassName =
    dday < 1
      ? 'font-bold flex justify-center items-center text-[50px] sm:text-[70px] w-[30px] h-[70px] sm:w-[35px] sm:h-[70px] rounded-[8px] text-center'
      : 'font-bold flex justify-center items-center text-[78px] sm:text-[110px] w-[63px] h-[93px] sm:w-[90px] sm:h-[134px] rounded-[8px] bg-white text-center'

  const handlePromoPopupCookie = (popupId: string, duration?: PROMO_POPUP_HIDE_DURATION) => {
    if (duration) {
      let cookieExpires: Date

      switch (duration) {
        case PROMO_POPUP_HIDE_DURATION.ONE_DAY: {
          cookieExpires = addDays(new Date(), 1)
          break
        }
        case PROMO_POPUP_HIDE_DURATION.ONE_WEEK: {
          cookieExpires = addDays(new Date(), 7)
          break
        }

        case PROMO_POPUP_HIDE_DURATION.ONE_MONTH: {
          cookieExpires = addMonths(new Date(), 1)
          break
        }
      }
      cookies.set(`is_closed_popup_${popupId}`, '1', {
        ...COOKIE_OPT,
        expires: cookieExpires,
      })
    }
  }

  const handleOnNavigateToUrl = (popupId: string, destUrl: string, option?: any) => {
    sendGTMEvent(GTM_EVENT.CLICK_ONSITE_POPUP, {
      popup_id: popupId,
      dest_url: destUrl,
      option,
    })

    if (destUrl && destUrl.length > 0) {
      if (destUrl.charAt(0) === '/') {
        router.push(`${destUrl}`, option)
      } else {
        router.push(`${destUrl}`, option)
      }
    } else {
      router.push(`/`)
    }
  }

  return (
    <div
      className="relative h-full w-full min-h-[520px] sm:min-h-[618px] bg-cover rounded-[12px]"
      style={{
        backgroundImage: isMobile
          ? `url("${
              isLocaleKO ? popupData?.popup_background_image?.mobile.ko : popupData?.popup_background_image?.mobile.en
            }")`
          : `url("${
              isLocaleKO ? popupData?.popup_background_image?.desktop.ko : popupData?.popup_background_image?.desktop.en
            }")`,
      }}
    >
      {/* Close Button */}
      <div
        className="absolute right-[16px] top-[16px] cursor-pointer"
        onClick={() => {
          popupClose({ id: popupData?.id })
        }}
      >
        <QIcon_close_default width={20} height={20} color={popupData?.button?.close_modal_x_button_color} />
      </div>
      {/* D Day Counter */}
      {popupData?.d_day_counter?.enabled && (
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            top: `${
              isMobile
                ? popupData?.d_day_counter?.gap_from_top_pixel_mobile
                : popupData?.d_day_counter?.gap_from_top_pixel_desktop
            }px`,
            color: `${popupData?.d_day_counter?.text_color}`,
          }}
        >
          <DdayCounter
            initialDay={new Date()}
            targetDay={date}
            types={dday < 1 ? [D_DAY_TYPE.HOUR, D_DAY_TYPE.MIN, D_DAY_TYPE.SEC] : [D_DAY_TYPE.DAY]}
            classNameTotalContainer="flex flex-row justify-center relative"
            classNameDigitContainer="flex flex-row justify-center gap-x-[4px] sm:gap-x-[8px]"
            classNameDigit={digitClassName}
            seperator={
              dday < 1 ? (
                <div className="mx-[6px] sm:mx-[8px] flex flex-col justify-center gap-y-[8px] sm:gap-y-[16px]">
                  <div
                    className="h-[10px] w-[10px] rounded-full"
                    style={{
                      backgroundColor: `${popupData?.d_day_counter?.text_color}`,
                    }}
                  />
                  <div
                    className="h-[10px] w-[10px] rounded-full"
                    style={{
                      backgroundColor: `${popupData?.d_day_counter?.text_color}`,
                    }}
                  />
                </div>
              ) : (
                <div className="mx-[6px] flex flex-col justify-center gap-y-[16px]">
                  <div
                    className="h-[8px] w-[8px] rounded-full bg-white"
                    style={{
                      backgroundColor: `${popupData?.d_day_counter?.text_color}`,
                    }}
                  />
                  <div
                    className="h-[8px] w-[8px] rounded-full bg-white"
                    style={{
                      backgroundColor: `${popupData?.d_day_counter?.text_color}`,
                    }}
                  />
                </div>
              )
            }
            typeLabel={null}
            classNameTypeLabel="font-medium text-center text-[14px] leading-[21px] mt-[4px]"
            text={null}
          />
        </div>
      )}
      {/* Buttons */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[24px] sm:bottom-[32px] flex justify-center items-center flex-col w-full">
        {/* CTA Button */}
        <div className="max-w-auto sm:max-w-[400px] w-full smd:px-[20px]">
          <DSButton
            type={BUTTON_TYPE.SOLID}
            size={BUTTON_SIZE.LG}
            customBgColor={popupData?.button?.text_bg_color}
            onClick={debounce(() => {
              handleOnNavigateToUrl(popupData?.id, popupData?.button?.url, {
                appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                appSendData: {
                  url: getFullUrl(`${popupData?.button?.app_url}?app=true`),
                },
              })
            }, 500)}
          >
            <DSButton.Label customTxtColor={popupData?.button?.text_color}>
              {isLocaleKO ? popupData?.button?.text.ko : popupData?.button?.text.en}
            </DSButton.Label>
          </DSButton>
        </div>
        {/* Close & Hide Button */}
        <div
          style={{ color: popupData?.button?.do_not_see_button_color || '' }}
          className="cursor-pointer Body1-16Md mt-[8px]"
          onClick={() => {
            popupClose({ id: popupData?.id })
            handlePromoPopupCookie(popupData?.id, PROMO_POPUP_HIDE_DURATION.ONE_WEEK)
          }}
        >
          {t(s_upcoming_lessons.welcomeBackModal.footerLeft)}
        </div>
      </div>
    </div>
  )
}

export default RemoteConfigPopupBody
